import { useState, useEffect } from "react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Mail, User, AtSign, Save } from "lucide-react";
import { Navigate } from "react-router-dom";

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user && typeof user === "object") {
      setName(user.name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  if (user === null) return null;
  if (!user || typeof user !== "object") return <Navigate to="/login" replace />;

  const dirty = (name || "") !== (user.name || "") || (email || "") !== (user.email || "");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      await updateProfile({ name: name.trim(), email: email.trim() });
      toast.success("Profile updated.");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div data-testid="profile-page" className="max-w-2xl mx-auto px-5 md:px-8 py-12">
        <span className="overline text-[var(--cm-primary)]">Your profile</span>
        <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 tracking-tight">
          Account settings
        </h1>
        <p className="text-ink-soft mt-2">
          Update how Bahoi knows you. Your username (<span className="font-semibold">@{user.username}</span>) can't be changed.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-5 card-soft p-7">
          <Field label="Display name" testid="profile-name-field" icon={User}>
            <input
              data-testid="profile-name-input"
              required
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-app border border-ink-soft rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-[var(--cm-primary)]/40"
            />
          </Field>
          <Field label="Username · locked" testid="profile-username-field" icon={AtSign}>
            <input
              data-testid="profile-username-input"
              disabled
              value={user.username}
              className="w-full bg-app/60 border border-ink-soft rounded-xl pl-10 pr-4 py-3 text-ink-soft cursor-not-allowed"
            />
          </Field>
          <Field label="Email · optional" testid="profile-email-field" icon={Mail}>
            <input
              data-testid="profile-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-app border border-ink-soft rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-[var(--cm-primary)]/40"
              placeholder="you@example.com"
            />
            <p className="text-xs text-ink-soft mt-1.5">
              We'll only use this if we need to reach out about your shifts or community updates. Leave blank to opt out.
            </p>
          </Field>

          {err && (
            <div data-testid="profile-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2.5 rounded-lg">
              {err}
            </div>
          )}

          <button
            data-testid="profile-save-btn"
            type="submit"
            disabled={saving || !dirty}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
          >
            {saving ? "Saving…" : (
              <>
                <Save className="h-4 w-4" /> Save changes
              </>
            )}
          </button>
        </form>
      </div>
    </Layout>
  );
}

function Field({ label, children, testid, icon: Icon }) {
  return (
    <label data-testid={testid} className="block">
      <span className="text-xs uppercase tracking-widest font-semibold text-ink-soft">{label}</span>
      <div className="mt-2 relative">
        {Icon && <Icon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft pointer-events-none" />}
        {children}
      </div>
    </label>
  );
}
